import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import {
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  type GridToolbarProps,
} from "@mui/x-data-grid-premium";
import { brandContainedButtonSx } from "../../../constants/brandContainedButtonStyles";
import { useBrandThemeColors } from "../../../branding";
import { useHasPrivilege } from "../../../hooks/useHasPrivilege";
import { OBJECT_TO_OBJECT_PRIVILEGES } from "../../../services/permissionsService";
import { AI_PLAN_ADD_OTO_PLAN_PATH } from "../constants";

type ObjectToObjectPlanTableToolbarProps = GridToolbarProps;

export default function ObjectToObjectPlanTableToolbar(
  _props: ObjectToObjectPlanTableToolbarProps,
) {
  const navigate = useNavigate();
  const { primaryColor } = useBrandThemeColors();
  const canManageOtoPlan = useHasPrivilege(OBJECT_TO_OBJECT_PRIVILEGES.manage);

  const gridToolbarFeatureButtonSlotProps = useMemo(
    () => ({
      button: {
        size: "small" as const,
        sx: {
          color: `${primaryColor} !important`,
          textTransform: "none",
          fontWeight: 500,
          "& .MuiSvgIcon-root": {
            color: `${primaryColor} !important`,
          },
          "& .MuiButton-startIcon .MuiSvgIcon-root": {
            color: `${primaryColor} !important`,
          },
          "&.Mui-disabled": {
            color: "rgba(0, 0, 0, 0.26) !important",
            "& .MuiSvgIcon-root": {
              color: "rgba(0, 0, 0, 0.26) !important",
            },
            "& .MuiButton-startIcon .MuiSvgIcon-root": {
              color: "rgba(0, 0, 0, 0.26) !important",
            },
          },
        },
      },
    }),
    [primaryColor],
  );

  return (
    <Box
      role="toolbar"
      sx={{
        p: "12px 0",
        bgcolor: "background.paper",
        boxShadow: "none",
        border: "none",
        borderBottom: "none",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: "#262626", fontSize: "16px" }}
        >
          Object to Object
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          "& .MuiButton-root:not(.ai-plan-add-new-button)": {
            color: "#595959 !important",
            textTransform: "none",
            fontWeight: 500,
          },
          "& .MuiButton-startIcon .MuiSvgIcon-root": {
            color: "inherit !important",
          },
        }}
      >
        {canManageOtoPlan ? (
          <Button
            variant="contained"
            size="small"
            className="ai-plan-add-new-button"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            sx={brandContainedButtonSx}
            onClick={() => navigate(AI_PLAN_ADD_OTO_PLAN_PATH)}
          >
            Add New
          </Button>
        ) : null}
        <GridToolbarDensitySelector
          slotProps={gridToolbarFeatureButtonSlotProps}
        />
        <GridToolbarColumnsButton
          slotProps={gridToolbarFeatureButtonSlotProps}
        />
      </Box>
    </Box>
  );
}
